import { Reducer } from 'redux';
import { createReducer, describeAction } from 'ts-describe-action';
import { SettingsStorage } from '../../api/settingsStorage';
import { ActionTypes } from '../actionTypes';
import { TutorialState, TutorialStep } from '../state';

const TUTORIAL_ENEMY_MAX_HP = 3;

const getEncounterDefaults = (): Pick<
  TutorialState,
  'checkOutcome' | 'lastCheckTotal' | 'enemyHp' | 'combatPhase' | 'combatOutcome' | 'lastCombatRoll'
> => ({
  checkOutcome: 'idle',
  lastCheckTotal: undefined,
  enemyHp: TUTORIAL_ENEMY_MAX_HP,
  combatPhase: 'aim',
  combatOutcome: 'idle',
  lastCombatRoll: undefined,
});

const setEnabled = describeAction(
  'SET_TUTORIAL_ENABLED',
  (prev: TutorialState, enabled: boolean): TutorialState => ({
    ...prev,
    enabled,
  }),
);

const setCompleted = describeAction(
  'SET_TUTORIAL_COMPLETED',
  (prev: TutorialState, completed: boolean): TutorialState => ({
    ...prev,
    completed,
  }),
);

const setDismissed = describeAction(
  'SET_TUTORIAL_DISMISSED',
  (prev: TutorialState, dismissed: boolean): TutorialState => ({
    ...prev,
    dismissed,
  }),
);

const setStep = describeAction(
  'SET_TUTORIAL_STEP',
  (prev: TutorialState, step: TutorialStep): TutorialState => ({
    ...prev,
    step,
    attemptsUsed: 0,
    ...getEncounterDefaults(),
  }),
);

const setAttemptsUsed = describeAction(
  'SET_TUTORIAL_ATTEMPTS_USED',
  (prev: TutorialState, attemptsUsed: number): TutorialState => ({
    ...prev,
    attemptsUsed,
  }),
);

const setEncounterState = describeAction(
  'SET_TUTORIAL_ENCOUNTER_STATE',
  (
    prev: TutorialState,
    encounterState: Partial<
      Pick<
        TutorialState,
        'checkOutcome' | 'lastCheckTotal' | 'enemyHp' | 'combatPhase' | 'combatOutcome' | 'lastCombatRoll'
      >
    >,
  ): TutorialState => ({
    ...prev,
    ...encounterState,
  }),
);

export const TutorialAction = {
  setEnabled,
  setCompleted,
  setDismissed,
  setStep,
  setAttemptsUsed,
  setEncounterState,
};

export type TutorialActionType = ActionTypes<typeof TutorialAction>;

const getInitialState = (settingsStorage: SettingsStorage): TutorialState => {
  const completed = settingsStorage.getTutorialCompleted();
  const dismissed = settingsStorage.getTutorialDismissed();
  const savedStep = settingsStorage.getTutorialStep();

  return {
    enabled: !completed && !dismissed,
    completed,
    dismissed,
    step: completed ? 'completed' : savedStep === 'completed' ? 'tap_to_roll' : savedStep,
    attemptsUsed: 0,
    ...getEncounterDefaults(),
  };
};

export const createTutorialReducer = (settingsStorage: SettingsStorage): Reducer<TutorialState> =>
  createReducer(Object.values(TutorialAction), getInitialState(settingsStorage));
